import { useEffect, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { RequireAuth } from "@/components/layout/RequireAuth";
import { useAuth } from "@/store/auth";
import { LoginPage } from "@/pages/auth/LoginPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/auth/PasswordRecoveryPage";
import { SignupPage } from "@/pages/auth/SignupPage";
import { AccountPage } from "@/pages/auth/AccountPage";
import { AdminActivatePage } from "@/pages/auth/AdminActivatePage";
import { AdminAccessPage } from "@/pages/auth/AdminAccessPage";
import { TermsPage } from "@/pages/legal/TermsPage";
import { PrivacyPage } from "@/pages/legal/PrivacyPage";
import { StartPage } from "@/pages/workshop/StartPage";
import { ScenariosPage } from "@/pages/workshop/HomePage";
import { NewScenarioPage } from "@/pages/workshop/NewScenarioPage";
import { DataPage } from "@/pages/workshop/DataPage";
import { WorkshopPage } from "@/pages/workshop/WorkshopPage";
import { ReportPage, SavedRunReportPage } from "@/pages/workshop/ReportPage";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { DashboardPage } from "@/pages/admin/DashboardPage";
import { UsersPage } from "@/pages/admin/UsersPage";
import { MasterPage } from "@/pages/admin/MasterPage";
import { AuditPage } from "@/pages/admin/AuditPage";
import { WorkspacePage } from "@/pages/workspace/WorkspacePage";
import { WorkspaceProjectPage } from "@/pages/workspace/WorkspaceProjectPage";
import { hasFeature } from "@/lib/license";

function EnterpriseRoute({ children }: { children: ReactNode }) {
  const user = useAuth((state) => state.user);
  return hasFeature("workspace", user?.license) ? children : <Navigate to="/app" replace />;
}

/**
 * 라우트.
 *   /login /signup /terms /privacy      누구나
 *   /app        산정 시작(경로 선택,새 산정)          로그인 필수
 *   /app/scenarios 내 시나리오(보관함,작업 중)         로그인 필수
 *   /app/new    새 시나리오(예시에서,빈 시나리오,파일) , /app/work 산정 화면(7단계) , /app/report 결과보고서 , /workspace 회사 작업공간 , /data 참고 데이터 , /account 내 계정
 *   /admin …                            관리자만 (현황,가입자,마스터 데이터,이력)
 */
export function App() {
  const load = useAuth((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/admin/activate" element={<AdminActivatePage />} />
        <Route path="/admin/access" element={<AdminAccessPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        <Route element={<RequireAuth />}>
          <Route path="/app" element={<StartPage />} />
          <Route path="/app/scenarios" element={<ScenariosPage />} />
          <Route path="/app/work" element={<WorkshopPage />} />
          <Route path="/app/report" element={<ReportPage />} />
          <Route path="/app/new" element={<NewScenarioPage />} />
          <Route path="/workspace" element={<EnterpriseRoute><WorkspacePage /></EnterpriseRoute>} />
          <Route path="/workspace/projects/:projectId/runs/:runId/report" element={<EnterpriseRoute><SavedRunReportPage /></EnterpriseRoute>} />
          <Route path="/workspace/projects/:projectId" element={<EnterpriseRoute><WorkspaceProjectPage /></EnterpriseRoute>} />
          <Route path="/examples" element={<Navigate to="/app/new" replace />} />
          <Route path="/data/:kind?" element={<DataPage />} />
          {/* 예전 주소 , 홈으로 */}
          <Route path="/app/start" element={<Navigate to="/app" replace />} />
          <Route path="/account" element={<AccountPage />} />
        </Route>

        <Route element={<RequireAuth admin />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="master/:kind?" element={<MasterPage />} />
            <Route path="audit" element={<AuditPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
